// useConversations — the one data hook the Standup view depends on.
//
// Fetches recent conversations from Supabase, pulls a short excerpt message
// for the "Needs You" bucket, and derives the Standup buckets:
//
//   needsYou      = status='open' and last_message_direction='inbound'
//   draftsReady   = (Phase D — empty for now, hook reserves the bucket)
//   snoozed       = status='snoozed' (Phase 5a — sorted by snoozed_until ASC)
//   followUps     = status='awaiting' and last_message_direction='outbound'
//                   and last_message_at older than 2 days
//   autoHandled   = status='resolved' and status_changed_at within last 7 days
//                   (Polish #7 widened the window from 24h so the section can
//                   group by Today / Yesterday / This week in the UI)
//
// Spam is excluded from every bucket (it has its own dedicated view, future).
//
// Brand filtering (all / md / hk) is applied to each bucket.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Brand, Channel, ConversationStatus, Direction } from '@md/shared';
import type { BrandFilter } from '@/components/TopNav';
import { isLikelySpam } from '@/lib/spam';
export type ConversationIntent =
  | 'tracking'
  | 'damage'
  | 'address_change'
  | 'cancel'
  | 'refund'
  | 'proof_change'
  | 'generic';

export type ConversationRow = {
  id: string;
  channel: Channel;
  brand: Brand;
  subject: string | null;
  customer_email: string | null;
  status: ConversationStatus;
  read_at: string | null;
  last_message_at: string;
  last_message_direction: Direction | null;
  // Phase 5a — null unless status='snoozed'.
  snoozed_until: string | null;
  // Phase 5a — stamped on every status transition; backfilled from last_message_at.
  status_changed_at: string;
  // Legacy column kept around for back-compat with the old auto-handled filter;
  // not used in the Phase 5a derivation but still selected so older callers
  // that read it from a row don't break.
  resolved_at: string | null;
  // Phase 9 — regex-derived intent for the latest inbound. May be null on
  // first load; the Standup hook calls /api/ai/intent/bulk to backfill.
  intent: ConversationIntent | null;
  intent_confidence: number | null;
  intent_meta: {
    evidencePhrase?: string | null;
    matchedPatterns?: string[];
    orderNumberHint?: string | null;
    classifiedFromMessageId?: string;
  } | null;
  intent_detected_at: string | null;
};
export type MessageExcerpt = {
  conversation_id: string;
  body: string;
  sender_name: string | null;
  direction: Direction;
};
export type StandupBuckets = {
  needsYou: ConversationRow[];
  followUps: ConversationRow[];
  autoHandled: ConversationRow[];
  // Phase 5a — sorted by snoozed_until ASC (soonest wake first).
  snoozed: ConversationRow[];
  draftsReady: ConversationRow[]; // always [] until Phase D wires drafts in
  // Phase 6a — conversations that would normally sit in Needs You but match
  // a high-confidence spam pattern. Hidden by default in the UI; Kimi can
  // toggle "Show N hidden" to see them. Not persisted; always recomputed
  // on each load, so retuning the matcher affects everything immediately.
  hiddenSpam: ConversationRow[];
  totalToday: number; // count of conversations whose last_message_at is today
  excerpts: Map<string, MessageExcerpt>;
};
const MS_PER_DAY = 86_400_000;
export function useConversations(brand: BrandFilter) {
  return useQuery<StandupBuckets>({
    queryKey: ['conversations', brand],
    queryFn: async () => fetchBuckets(brand),
    staleTime: 30_000,
  });
}
async function fetchBuckets(brand: BrandFilter): Promise<StandupBuckets> {
  // 1. Base conversations query — last 30 days, ordered newest first.
  const since = new Date(Date.now() - 30 * MS_PER_DAY).toISOString();
  let q = supabase
    .from('conversations')
    .select(
      'id, channel, brand, subject, customer_email, status, read_at, last_message_at, last_message_direction, snoozed_until, status_changed_at, resolved_at, intent, intent_confidence, intent_meta, intent_detected_at',
    )
    .gte('last_message_at', since)
    // Polish #4 — merged rows are ghosts; don't show them anywhere.
    .is('merged_into_id', null)
    .order('last_message_at', { ascending: false })
    .limit(200);
  if (brand !== 'all') q = q.eq('brand', brand);
  const { data: rows, error } = await q;
  if (error) throw error;
  const conversations = (rows ?? []) as ConversationRow[];
  const now = Date.now();
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  // 2. Derive buckets.
  const needsYouRaw = conversations.filter(
    (c) => c.status === 'open' && c.last_message_direction === 'inbound',
  );
  // Two paths into Circle back:
  //   1. You marked a thread awaiting (without replying) — show immediately
  //      so it doesn't disappear from your view.
  //   2. You replied 2+ days ago and they haven't responded.
  const followUps = conversations
    .filter((c) => {
      if (c.status !== 'awaiting') return false;
      if (c.last_message_direction === 'inbound') return true;
      const age = now - new Date(c.last_message_at).getTime();
      return age >= 2 * MS_PER_DAY;
    })
    .sort(
      (a, b) =>
        new Date(a.last_message_at).getTime() - new Date(b.last_message_at).getTime(),
    );
  // Polish #7 — widen the resolved window to 7 days so the Standup section
  // can group by Today / Yesterday / Earlier this week. The 24h rule was
  // fine when this was a one-line "here's what moved while you slept" rail,
  // but grouping gives it more meaning as a trailing-week record.
  const autoHandled = conversations
    .filter((c) => {
      if (c.status !== 'resolved') return false;
      const age = now - new Date(c.status_changed_at).getTime();
      return age <= 7 * MS_PER_DAY;
    })
    .sort(
      (a, b) =>
        new Date(b.status_changed_at).getTime() - new Date(a.status_changed_at).getTime(),
    );
  const snoozed = conversations
    .filter((c) => c.status === 'snoozed' && !!c.snoozed_until)
    .sort((a, b) => {
      // Soonest wake first.
      const at = a.snoozed_until ? new Date(a.snoozed_until).getTime() : Number.MAX_SAFE_INTEGER;
      const bt = b.snoozed_until ? new Date(b.snoozed_until).getTime() : Number.MAX_SAFE_INTEGER;
      return at - bt;
    });
  const totalToday = conversations.filter(
    (c) => new Date(c.last_message_at).getTime() >= startOfToday.getTime(),
  ).length;
  // 3. For the first ~20 "needs you" candidates, fetch a latest inbound
  //    message so we can render an excerpt on each decide-card AND run the
  //    spam matcher (which needs the body). We pull a few more than the
  //    display cap of 12 because some will be hidden by the matcher — if we
  //    capped at exactly 12, a heavy-spam day would leave fewer than 12
  //    real cards showing.
  const excerpts = new Map<string, MessageExcerpt>();
  const needIds = needsYouRaw.slice(0, 24).map((c) => c.id);
  if (needIds.length > 0) {
    const { data: msgs, error: msgErr } = await supabase
      .from('messages')
      .select('conversation_id, body, sender_name, direction, sent_at')
      .in('conversation_id', needIds)
      .eq('direction', 'inbound')
      .order('sent_at', { ascending: false });
    if (msgErr) throw msgErr;
    for (const m of msgs ?? []) {
      // First row per conversation (query is ordered desc, so first wins).
      if (!excerpts.has(m.conversation_id)) {
        excerpts.set(m.conversation_id, {
          conversation_id: m.conversation_id,
          body: m.body,
          sender_name: m.sender_name,
          direction: m.direction,
        });
      }
    }
  }

  // 4. Phase 6a — split needsYou into "real" + "hiddenSpam" using the
  //    conservative matcher. Only conversations we have an excerpt for get
  //    checked; anything outside the excerpt window stays in needsYou by
  //    default (safer than silently hiding something we can't inspect).
  const needsYou: ConversationRow[] = [];
  const hiddenSpam: ConversationRow[] = [];
  for (const c of needsYouRaw) {
    const excerpt = excerpts.get(c.id);
    if (excerpt && isLikelySpam({ subject: c.subject, body: excerpt.body })) {
      hiddenSpam.push(c);
    } else {
      needsYou.push(c);
    }
  }

  return {
    needsYou,
    followUps,
    autoHandled,
    snoozed,
    draftsReady: [],
    hiddenSpam,
    totalToday,
    excerpts,
  };
}
